/**
 * Excel Core Engine — APV ERP Standard
 * Provides standardized Excel export, template generation, and workbook parsing using ExcelJS & SheetJS.
 * Strictly replaces CSV usage across the entire enterprise system.
 */
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { validateExcelFile } from '@/lib/utils';

// ── Types ──────────────────────────────────────────────

export interface ExcelColumnDef<T = any> {
  key: string;
  /** Label displayed in header row. 'header' is also accepted for backward compatibility */
  label: string;
  header?: string;
  width?: number;
  type?: 'string' | 'number' | 'int' | 'decimal' | 'date' | 'enum';
  /** Value formatter for export — function hoặc preset string ("currency", "date", "percent", "number") */
  format?: ((value: any, row?: T) => string | number) | 'currency' | 'date' | 'percent' | 'number';
  /** Legacy alias cho format (function) */
  formatter?: (value: any, row?: T) => string | number;
  /** Legacy alias for format */
  exportFn?: (row: T) => string | number;
  required?: boolean;
  enumValues?: string[];
  /** false = cột chỉ xuất (VD tồn kho): không có trong file mẫu, bỏ qua khi nhập (04/11/2026) */
  importable?: boolean;
  /** Số nhỏ nhất cho cột số khi nhập (VD 0 = không nhận số âm) */
  min?: number;
  /** Định dạng số riêng khi xuất (VD '0.######' cho hệ số quy đổi nhiều số lẻ) */
  numFmt?: string;
}

export interface ExportToExcelOptions {
  sheetName?: string;
  creatorName?: string;
}

export interface ParseExcelResult {
  headers: string[];
  rows: Record<string, string>[];
  totalRows: number;
}

export interface ExcelValidationError {
  row: number;
  column: string;
  message: string;
}

export interface ExcelValidateResult {
  validRows: Record<string, unknown>[];
  errors: ExcelValidationError[];
  totalRows: number;
  validCount: number;
  errorCount: number;
}

// ── Helpers ───────────────────────────────────────────

export function formatDateForFile(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const h = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `${y}${m}${d}_${h}${min}`;
}

function applyHeaderStyle(row: ExcelJS.Row, colCount?: number) {
  row.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFFFF' } }; // Crisp White on Deep Navy
  row.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF1E3A8A' }, // Navy đậm (#1E3A8A)
  };
  row.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  row.height = 28;
  const totalCols = colCount || row.cellCount;
  for (let c = 1; c <= totalCols; c++) {
    const cell = row.getCell(c);
    cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1E3A8A' },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FF1E3A8A' } },
      left: { style: 'thin', color: { argb: 'FF60A5FA' } },
      bottom: { style: 'medium', color: { argb: 'FF0F172A' } },
      right: { style: 'thin', color: { argb: 'FF60A5FA' } },
    };
  }
}

function applyDataStyle(row: ExcelJS.Row, isEven: boolean = false, colCount?: number) {
  row.font = { name: 'Arial', size: 10, color: { argb: 'FF0F172A' } }; // slate-900
  row.alignment = { vertical: 'middle', wrapText: false };
  row.height = 22;

  const bgArgb = isEven ? 'FFF8FAFC' : 'FFFFFFFF'; // Zebra striping (#F8FAFC vs #FFFFFF)
  const totalCols = colCount || row.cellCount;

  for (let c = 1; c <= totalCols; c++) {
    const cell = row.getCell(c);
    cell.font = { name: 'Arial', size: 10, color: { argb: 'FF0F172A' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: bgArgb },
    };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FF94A3B8' } },    // Crisp visible slate-400 border
      left: { style: 'thin', color: { argb: 'FF94A3B8' } },
      bottom: { style: 'thin', color: { argb: 'FF94A3B8' } },
      right: { style: 'thin', color: { argb: 'FF94A3B8' } },
    };
  }
}

/**
 * Access nested properties safely, e.g. 'customer.name'
 */
function getNestedValue(obj: any, key: string): any {
  if (!obj || typeof obj !== 'object') return undefined;
  if (!key || typeof key !== 'string') return undefined;
  if (!key.includes('.')) return obj[key];
  return key.split('.').reduce((acc, part) => (acc !== null && acc !== undefined ? acc[part] : undefined), obj);
}

/**
 * Distinguish an array of ExcelColumnDef ({ key, label/header }) from an array of
 * entity records that happen to contain a `label` field (e.g. MaterialType / ConfigItem).
 */
export function isExcelColumnDefArray(arr: any): boolean {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  const first = arr[0];
  if (!first || typeof first !== 'object') return false;
  return (
    typeof first.key === 'string' &&
    first.key.length > 0 &&
    ('label' in first || 'header' in first) &&
    !('id' in first && 'code' in first && !('header' in first))
  );
}

// ── Export to Excel ───────────────────────────────────

/**
 * Canonical export to Excel function
 */
export async function exportToExcel<T = any>(
  arg1: any,
  arg2: any,
  filename: string,
  options?: ExportToExcelOptions
): Promise<void> {
  // Support both (data, columns, filename) and legacy inverted (columns, data, filename)
  let data: T[] = [];
  let columns: ExcelColumnDef<T>[] = [];

  if (Array.isArray(arg1) && Array.isArray(arg2)) {
    if (isExcelColumnDefArray(arg1) && !isExcelColumnDefArray(arg2)) {
      columns = arg1;
      data = arg2;
    } else {
      data = arg1;
      columns = arg2;
    }
  } else {
    data = Array.isArray(arg1) ? arg1 : [];
    columns = Array.isArray(arg2) ? arg2 : [];
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = options?.creatorName || 'APV ERP System';
  workbook.created = new Date();

  const sheetName = options?.sheetName || 'Dữ Liệu';
  const worksheet = workbook.addWorksheet(sheetName, {
    views: [{ state: 'frozen', ySplit: 1, showGridLines: true }],
  });

  // Track max character length per column for auto-fitting width
  const colMaxLengths: number[] = columns.map((col) => {
    const title = String(col?.label || col?.header || col?.key || '');
    return Math.max(title.length + 5, 14);
  });

  // Setup column definitions
  worksheet.columns = columns.map((col, idx) => {
    const title = String(col?.label || col?.header || col?.key || '');
    return {
      header: title,
      key: col?.key || `col_${idx}`,
      width: col?.width || colMaxLengths[idx],
    };
  });

  // Style header row
  const headerRow = worksheet.getRow(1);
  applyHeaderStyle(headerRow, columns.length);

  if (columns.length > 0) {
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: columns.length },
    };
  }

  // Add data rows
  data.forEach((item: any, index: number) => {
    const rowData: Record<string, any> = {};

    columns.forEach((col, colIdx) => {
      let val: any;

      const rawVal = getNestedValue(item, col.key);

      if (typeof col.format === 'function') {
        val = col.format(rawVal, item);
      } else if (typeof col.formatter === 'function') {
        val = col.formatter(rawVal, item);
      } else if (col.format === 'currency') {
        // Ghi SỐ (hiển thị #,##0 bên dưới) — chuỗi "1.000" khiến Excel coi là văn bản, không cộng / lọc được
        const num = Number(rawVal);
        val = isNaN(num) ? (rawVal ?? '') : num;
      } else if (col.format === 'date') {
        val = rawVal ? new Date(rawVal).toLocaleDateString('vi-VN') : '';
      } else if (col.format === 'percent') {
        const num = Number(rawVal);
        val = isNaN(num) ? (rawVal ?? '') : `${num}%`;
      } else if (col.exportFn) {
        val = col.exportFn(item);
      } else {
        val = rawVal;
      }

      if (col.type === 'number' || col.type === 'int' || col.type === 'decimal') {
        const num = Number(val);
        rowData[col.key] = isNaN(num) ? (val ?? '') : num;
      } else {
        rowData[col.key] = val ?? '';
      }

      const strLen = String(rowData[col.key] ?? '').length;
      if (strLen + 4 > colMaxLengths[colIdx]) {
        colMaxLengths[colIdx] = Math.min(strLen + 4, 52);
      }
    });

    const newRow = worksheet.addRow(rowData);
    applyDataStyle(newRow, index % 2 === 1, columns.length);

    // Apply specific number formatting and alignments
    columns.forEach((col) => {
      const cell = newRow.getCell(col.key);
      if (col.numFmt && typeof cell.value === 'number') {
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
        cell.numFmt = col.numFmt;
      } else if (col.format === 'currency' && typeof cell.value === 'number') {
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
        cell.numFmt = '#,##0';
      } else if (col.type === 'number' || col.type === 'int') {
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
        if (typeof cell.value === 'number') {
          cell.numFmt = '#,##0';
        }
      } else if (col.type === 'decimal') {
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
        if (typeof cell.value === 'number') {
          cell.numFmt = '#,##0.00';
        }
      } else if (col.type === 'date') {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      } else {
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      }
    });
  });

  // Apply dynamic auto-fit widths
  columns.forEach((col, idx) => {
    const sheetCol = worksheet.getColumn(idx + 1);
    if (sheetCol) {
      sheetCol.width = col.width ? Math.max(col.width, colMaxLengths[idx]) : colMaxLengths[idx];
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `${filename}_${formatDateForFile()}.xlsx`);
}

// ── Template Download ─────────────────────────────────

/**
 * File mẫu nhập liệu (chuẩn 04/11/2026): chỉ 1 dòng tiêu đề tô màu ở dòng 1, tiêu đề trùng file xuất,
 * cột bắt buộc tô màu riêng + ghi chú ô; không dòng gợi ý / dòng mẫu (dòng mẫu hay bị nhập nhầm thành dữ liệu).
 * Gợi ý kiểu dữ liệu / giá trị hợp lệ nằm trong ghi chú của ô tiêu đề. Cột importable: false không có trong mẫu.
 * Tham số sampleData giữ cho tương thích, không còn ghi vào file.
 */
export async function downloadExcelTemplate<T = any>(
  columns: ExcelColumnDef<T>[],
  filename: string,
  _sampleData?: Record<string, string | number>[]
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'APV ERP System';
  const worksheet = workbook.addWorksheet('Mẫu Nhập Liệu', {
    views: [{ state: 'frozen', ySplit: 1, showGridLines: true }],
  });
  const importCols = columns.filter((col) => col.importable !== false);

  worksheet.columns = importCols.map((col) => {
    const title = col.label || col.header || col.key;
    return { header: title, key: col.key, width: col.width || Math.max(title.length + 6, 16) };
  });

  const headerRow = worksheet.getRow(1);
  applyHeaderStyle(headerRow, importCols.length);
  importCols.forEach((col, idx) => {
    const cell = headerRow.getCell(idx + 1);
    const notes: string[] = [];
    if (col.required) {
      notes.push('Bắt buộc');
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB45309' } }; // amber-700: cột bắt buộc
    }
    if (col.type === 'number' || col.type === 'decimal' || col.type === 'int') {
      notes.push(col.min !== undefined && col.min >= 0 ? 'Số, không âm' : 'Số');
    }
    if (col.type === 'date') notes.push('Ngày (dd/mm/yyyy)');
    if (col.enumValues?.length) notes.push(`Một trong: ${col.enumValues.join(', ')}`);
    if (notes.length) cell.note = notes.join(' · ');
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `Mau_Nhap_${filename}.xlsx`);
}

// ── Excel Parser ──────────────────────────────────────

/**
 * Parses an Excel (.xlsx / .xls) file into standardized rows and headers.
 */
export async function parseExcelFile(file: File): Promise<ParseExcelResult> {
  const validation = validateExcelFile(file);
  if (!validation.valid) throw new Error(validation.error);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const XLSX = (await import('xlsx')) as any;
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });

  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    return { headers: [], rows: [], totalRows: 0 };
  }

  const sheet = workbook.Sheets[sheetName];
  const jsonData: string[][] = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });
  // Ô số (không phải ngày) lấy giá trị thật thay vì chữ đã định dạng: "#,##0.00" làm 4,645221 thành "4.65" (04/11/2026)
  const rawData: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true });
  rawData.forEach((rawRow, r) => {
    (rawRow || []).forEach((v, c) => {
      const shown = String(jsonData[r]?.[c] ?? '');
      // Giữ chữ hiển thị cho ngày / giờ / %, và mã số có số 0 đầu (VD 00123)
      if (typeof v === 'number' && r > 0 && !/[/:%]/.test(shown) && !/^0\d/.test(shown.trim()) && jsonData[r]) jsonData[r][c] = String(v);
    });
  });

  if (jsonData.length < 1) {
    return { headers: [], rows: [], totalRows: 0 };
  }

  // Normalize header names (remove asterisk markings and trim)
  const headers = jsonData[0].map((h) => String(h).replace(/\s*\(\*\)\s*$/, '').trim());

  // Check if row 2 is a type hint guide row (e.g. "text", "number", "date")
  let dataStartIndex = 1;
  if (jsonData.length > 1) {
    const secondRowFirstCell = String(jsonData[1][0] || '').toLowerCase().trim();
    if (/^(text|number|decimal|int|date|enum|string)/.test(secondRowFirstCell)) {
      dataStartIndex = 2;
    }
  }

  const rows = jsonData
    .slice(dataStartIndex)
    .map((rowArr) => {
      const row: Record<string, string> = {};
      headers.forEach((h, i) => {
        if (h) {
          row[h] = String(rowArr[i] ?? '').trim();
        }
      });
      return row;
    })
    .filter((row) => Object.values(row).some((v) => v !== ''));

  return { headers, rows, totalRows: rows.length };
}

// ── Row Validation ────────────────────────────────────

/**
 * Validates parsed Excel rows against defined column rules.
 */
export function validateExcelRows(
  rows: Record<string, string>[],
  columns: ExcelColumnDef[]
): ExcelValidateResult {
  const validRows: Record<string, unknown>[] = [];
  const errors: ExcelValidationError[] = [];

  // Build label / key lookups
  const labelToCol = new Map<string, ExcelColumnDef>();
  columns.forEach((col) => {
    labelToCol.set(col.label, col);
    if (col.header) labelToCol.set(col.header, col);
    labelToCol.set(col.key, col);
  });

  rows.forEach((row, rowIdx) => {
    const lineNum = rowIdx + 2; // +1 for header, +1 for 1-based index
    const transformedRow: Record<string, unknown> = {};
    let rowHasError = false;

    columns.forEach((col) => {
      // Cột chỉ xuất (tồn kho…) không nhận khi nhập — file xuất nhập lại không đổi dữ liệu đó
      if (col.importable === false) return;
      const rawVal = row[col.label] ?? (col.header ? row[col.header] : undefined) ?? row[col.key] ?? '';
      const strVal = String(rawVal).trim();

      // Required check
      if (col.required && !strVal) {
        errors.push({
          row: lineNum,
          column: col.label,
          message: `Trường "${col.label}" là bắt buộc.`,
        });
        rowHasError = true;
        return;
      }

      if (!strVal) {
        transformedRow[col.key] = null;
        return;
      }

      // Type checks
      switch (col.type) {
        case 'number':
        case 'decimal': {
          const cleanNumStr = strVal.replace(/[,\s]/g, '');
          const num = parseFloat(cleanNumStr);
          if (isNaN(num)) {
            errors.push({
              row: lineNum,
              column: col.label,
              message: `"${strVal}" không phải là số hợp lệ.`,
            });
            rowHasError = true;
          } else if (col.min !== undefined && num < col.min) {
            errors.push({ row: lineNum, column: col.label, message: `"${strVal}" phải ≥ ${col.min}.` });
            rowHasError = true;
          } else {
            transformedRow[col.key] = num;
          }
          break;
        }
        case 'int': {
          const cleanNumStr = strVal.replace(/[,\s]/g, '');
          const intVal = parseInt(cleanNumStr, 10);
          if (isNaN(intVal)) {
            errors.push({
              row: lineNum,
              column: col.label,
              message: `"${strVal}" không phải số nguyên hợp lệ.`,
            });
            rowHasError = true;
          } else if (col.min !== undefined && intVal < col.min) {
            errors.push({ row: lineNum, column: col.label, message: `"${strVal}" phải ≥ ${col.min}.` });
            rowHasError = true;
          } else {
            transformedRow[col.key] = intVal;
          }
          break;
        }
        case 'enum': {
          if (col.enumValues && col.enumValues.length > 0) {
            const matched = col.enumValues.find((v) => v.toLowerCase() === strVal.toLowerCase());
            if (!matched) {
              errors.push({
                row: lineNum,
                column: col.label,
                message: `"${strVal}" không nằm trong danh sách: ${col.enumValues.join(', ')}.`,
              });
              rowHasError = true;
            } else {
              transformedRow[col.key] = matched;
            }
          } else {
            transformedRow[col.key] = strVal;
          }
          break;
        }
        default:
          transformedRow[col.key] = strVal;
      }
    });

    if (!rowHasError) {
      validRows.push(transformedRow);
    }
  });

  return {
    validRows,
    errors,
    totalRows: rows.length,
    validCount: validRows.length,
    errorCount: errors.length,
  };
}
